import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import saveAs from "file-saver";

import { useOktaAuth } from "../hooks/useOktaAuth";
import {
  PRODUCT_STATUS,
  ProductDetails,
  ProductInventory,
  ProductRequest,
} from "../models/Product";
import {
  API_GATEWAY_ID_PROD_SERVICE,
  CLOUDFRONT_ASSET_BASE_URL,
} from "../utils/env.config";

import {
  RequestFactory,
  WithAccessToken,
  fetchAndParse,
  fetchAndParseJson,
} from "./index";

const prodServiceGatewayHeader = API_GATEWAY_ID_PROD_SERVICE && {
  "x-apigw-api-id": API_GATEWAY_ID_PROD_SERVICE,
};

//#region Types

type GetProductDetails = {
  productId?: string;
};

type CreateProductRequest = {
  productRequest: ProductRequest;
};

type UpdateProductRequest = {
  productId: string;
  productRequest: ProductRequest;
};

type UpdateProductStatus = {
  productId: string;
  status: PRODUCT_STATUS;
  reason?: string;
};

type GetProductInventory = {
  productId?: string;
};

type ImageUploadRequest = {
  filename: string;
  countryCode: string;
};

type ImageUploadResponse = {
  uploadUrl: string;
  filename: string;
};

type PutImageRequest = {
  image: File;
  countryCode: string;
};

type CsvUploadRequest = {
  productId?: string;
};

type CsvUploadResponse = {
  presignedUrl: string;
};

type UpdateVouchersRequest = {
  productId?: string;
};

type StatusCountResponse = Record<Partial<PRODUCT_STATUS>, number>;

//#endregion

//#region Fetch methods
const fetchAllProducts = ({
  accessToken,
  queryString,
}: WithAccessToken<{
  queryString?: string;
}>): Promise<ProductDetails[]> => {
  const getAllProductsRequest = RequestFactory.createGetRequest(
    `/products${queryString ? queryString : ""}`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        ...prodServiceGatewayHeader,
      },
    },
  );
  return fetch(getAllProductsRequest).then(response => response.json());
};

const fetchProductDetails = ({
  productId,
  accessToken,
}: WithAccessToken<GetProductDetails>): Promise<ProductDetails> => {
  const request = RequestFactory.createGetRequest(`/products/${productId}`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      ...prodServiceGatewayHeader,
    },
  });
  return fetchAndParseJson<ProductDetails>(request);
};

const fetchProductInventory = ({
  productId,
  accessToken,
}: WithAccessToken<GetProductInventory>): Promise<ProductInventory> => {
  const request = RequestFactory.createGetRequest(
    `/products/${productId}/inventory`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        ...prodServiceGatewayHeader,
      },
    },
  );
  return fetchAndParseJson(request);
};

const fetchProductStatusCount = (accessToken?: string) => {
  const request = RequestFactory.createGetRequest("/products/statusCount", {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      ...prodServiceGatewayHeader,
    },
  });
  return fetchAndParseJson<StatusCountResponse>(request);
};

const createProduct = ({
  productRequest,
  accessToken,
}: WithAccessToken<CreateProductRequest>) => {
  const request = RequestFactory.createPostRequest<ProductRequest>(
    "/products",
    productRequest,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        ...prodServiceGatewayHeader,
      },
    },
  );

  return fetchAndParseJson<ProductDetails>(request);
};

const updateProduct = ({
  productId,
  productRequest,
  accessToken,
}: WithAccessToken<UpdateProductRequest>) => {
  const request = RequestFactory.createPatchRequest<ProductRequest>(
    `/products/${productId}`,
    productRequest,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        ...prodServiceGatewayHeader,
      },
    },
  );

  return fetchAndParseJson<ProductDetails>(request);
};

const updateProductStatus = ({
  productId,
  status,
  accessToken,
  reason,
}: WithAccessToken<UpdateProductStatus>) => {
  type Request = {
    status: PRODUCT_STATUS;
    reason?: string;
  };

  const request = RequestFactory.createPatchRequest<Request>(
    `/products/${productId}/status`,
    { status, reason },
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        ...prodServiceGatewayHeader,
      },
    },
  );

  return fetchAndParse(request);
};

const approveProduct = ({
  productId,
  accessToken,
}: WithAccessToken<Pick<UpdateProductStatus, "productId">>) => {
  const request = RequestFactory.createPatchRequest(
    `/products/${productId}/approve`,
    {},
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        ...prodServiceGatewayHeader,
      },
    },
  );
  return fetchAndParse(request);
};

export const getImagePreSignedUrl = ({
  filename,
  countryCode,
  accessToken,
}: WithAccessToken<ImageUploadRequest>): Promise<ImageUploadResponse> => {
  const request = RequestFactory.createGetRequest(
    `/upload/url?filename=${filename}&country-code=${countryCode}`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        ...prodServiceGatewayHeader,
      },
    },
  );

  return fetchAndParseJson(request);
};

export const getCsvPreSignedUrl = ({
  productId,
  accessToken,
}: WithAccessToken<CsvUploadRequest>): Promise<CsvUploadResponse> => {
  const request = RequestFactory.createGetRequest(
    `/buckets/products/${productId}/vouchers/presignedurls/uploadurl`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        ...prodServiceGatewayHeader,
      },
    },
  );

  return fetchAndParseJson(request);
};

export const getCsvPreSignedDownloadUrl = ({
  productId,
  accessToken,
}: WithAccessToken<CsvUploadRequest>): Promise<CsvUploadResponse> => {
  const request = RequestFactory.createGetRequest(
    `/buckets/products/${productId}/vouchers/presignedurls/downloadurl`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        ...prodServiceGatewayHeader,
      },
    },
  );

  return fetchAndParseJson(request);
};

export const getSpecificUserCsvPreSignedUrl = ({
  productId,
  accessToken,
}: WithAccessToken<CsvUploadRequest>): Promise<CsvUploadResponse> => {
  const request = RequestFactory.createGetRequest(
    `/buckets/products/${productId}/users/presignedurls/uploadurl`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        ...prodServiceGatewayHeader,
      },
    },
  );

  return fetchAndParseJson(request);
};

export const getSpecificUserCsvPreSignedDownloadUrl = ({
  productId,
  accessToken,
}: WithAccessToken<CsvUploadRequest>): Promise<CsvUploadResponse> => {
  const request = RequestFactory.createGetRequest(
    `/buckets/products/${productId}/users/presignedurls/downloadurl`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        ...prodServiceGatewayHeader,
      },
    },
  );

  return fetchAndParseJson(request);
};

//#endregion

//#region Queries
export const useGetAllProducts = (queryString?: string) => {
  const { accessToken } = useOktaAuth();

  return useQuery(
    ["products", queryString],
    () => fetchAllProducts({ accessToken, queryString }),
    {
      enabled: !!accessToken,
    },
  );
};

export const useGetProductDetails = (request: GetProductDetails) => {
  const { accessToken } = useOktaAuth();

  return useQuery<ProductDetails>(
    ["product", request.productId],
    () => fetchProductDetails({ ...request, accessToken }),
    {
      enabled: !!request.productId,
    },
  );
};

export const useGetProductInventory = ({ productId }: GetProductInventory) => {
  const { accessToken } = useOktaAuth();

  return useQuery(
    ["product", "inventory", productId],
    () => fetchProductInventory({ productId, accessToken }),
    {
      enabled: !!productId && !!accessToken,
    },
  );
};

export const useGetProductStatusCount = () => {
  const { accessToken } = useOktaAuth();

  return useQuery(
    ["product", "status", "count"],
    () => fetchProductStatusCount(accessToken),
    {
      enabled: !!accessToken,
    },
  );
};

export const useCreateProduct = () => {
  const { accessToken } = useOktaAuth();

  return useMutation<ProductDetails, unknown, CreateProductRequest>({
    mutationFn: variables => createProduct({ ...variables, accessToken }),
  });
};

export const usePatchProduct = () => {
  const queryClient = useQueryClient();
  const { accessToken } = useOktaAuth();

  return useMutation<ProductDetails, unknown, UpdateProductRequest>({
    mutationFn: variables => updateProduct({ ...variables, accessToken }),
    onSuccess: data =>
      Promise.all([
        queryClient.invalidateQueries(["products"]),
        queryClient.invalidateQueries(["product", data.productId]),
      ]),
  });
};

export const usePatchProductStatus = () => {
  const queryClient = useQueryClient();
  const { accessToken } = useOktaAuth();

  return useMutation<unknown, unknown, UpdateProductStatus>({
    mutationFn: variables => updateProductStatus({ ...variables, accessToken }),

    onSuccess: (_data, variables) =>
      Promise.all([
        queryClient.invalidateQueries(["products"]),
        queryClient.invalidateQueries(["product", variables.productId]),
        queryClient.invalidateQueries(["product", "status", "count"]),
      ]),
  });
};

export const usePutImageUpload = () => {
  const { accessToken } = useOktaAuth();

  return useMutation({
    mutationFn: async ({ image, countryCode }: PutImageRequest) => {
      const { uploadUrl, filename } = await getImagePreSignedUrl({
        filename: image.name,
        countryCode,
        accessToken,
      });

      const request = RequestFactory.createPutRequest(uploadUrl, {
        body: new Blob([image], { type: image.type }),
        headers: {
          "Content-Type": image.type,
        },
      });

      await fetchAndParse(request);
      return `${CLOUDFRONT_ASSET_BASE_URL}/${filename}`;
    },
  });
};

export const usePutCsvUpload = (productId?: string) => {
  const { accessToken } = useOktaAuth();

  return useMutation({
    mutationFn: async (csvFile: File) => {
      const { presignedUrl } = await getCsvPreSignedUrl({
        productId,
        accessToken,
      });

      const request = RequestFactory.createPutRequest(presignedUrl, {
        body: csvFile,
        headers: {
          "Content-Type": csvFile.type,
          "Content-Disposition": `attachment; filename="latest.csv"`,
        },
      });

      await fetchAndParse(request);
    },
  });
};

export const useGetCsvDownload = (productId?: string) => {
  const { accessToken } = useOktaAuth();

  return useMutation({
    mutationFn: async () => {
      const { presignedUrl } = await getCsvPreSignedDownloadUrl({
        productId,
        accessToken,
      });

      const request = RequestFactory.createGetCsvRequest(presignedUrl, {
        headers: {
          ...prodServiceGatewayHeader,
        },
      });

      const response = await fetchAndParse(request);
      const blob = await response.blob();
      saveAs(blob, "latest.csv");
    },
  });
};

export const useApproveProduct = () => {
  const { accessToken } = useOktaAuth();
  const queryClient = useQueryClient();

  return useMutation<unknown, unknown, Pick<UpdateProductStatus, "productId">>({
    mutationFn: variables => approveProduct({ ...variables, accessToken }),
    onSuccess: (_data, variables) =>
      Promise.all([
        queryClient.invalidateQueries(["products"]),
        queryClient.invalidateQueries(["product", variables.productId]),
        queryClient.invalidateQueries(["product", "status", "count"]),
      ]),
  });
};

//#endregion

export const usePutSpecificUserCsvUpload = (productId?: string) => {
  const { accessToken } = useOktaAuth();

  return useMutation({
    mutationFn: async (csvFile: File) => {
      const { presignedUrl } = await getSpecificUserCsvPreSignedUrl({
        productId,
        accessToken,
      });

      const request = RequestFactory.createPutRequest(presignedUrl, {
        body: csvFile,
        headers: {
          "Content-Type": csvFile.type,
          "Content-Disposition": `attachment; filename="latest.csv"`,
        },
      });

      await fetchAndParse(request);
    },
  });
};

export const useGetPutSpecificUserCsvDownload = (productId?: string) => {
  const { accessToken } = useOktaAuth();

  return useMutation({
    mutationFn: async () => {
      const { presignedUrl } = await getSpecificUserCsvPreSignedDownloadUrl({
        productId,
        accessToken,
      });

      const request = RequestFactory.createGetCsvRequest(presignedUrl, {
        headers: {
          ...prodServiceGatewayHeader,
        },
      });

      const response = await fetchAndParse(request);
      const blob = await response.blob();
      saveAs(blob, "latest.csv");
    },
  });
};

export const getExtractRemainingCodes = ({
  productId,
  accessToken,
}: WithAccessToken<CsvUploadRequest>): Promise<CsvUploadResponse> => {
  const request = RequestFactory.createGetRequest(
    `/products/${productId}/vouchers/download`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        ...prodServiceGatewayHeader,
      },
    },
  );

  return fetchAndParseJson(request);
};

export const useExtractRemainingCodes = (productId?: string) => {
  const { accessToken } = useOktaAuth();

  return useMutation({
    mutationFn: async () => {
      const { presignedUrl } = await getExtractRemainingCodes({
        productId,
        accessToken,
      });
      if (!presignedUrl) {
        throw new Error("no remaining codes");
      }
      const request = RequestFactory.createGetCsvRequest(presignedUrl, {
        headers: {
          ...prodServiceGatewayHeader,
        },
      });
      const response = await fetchAndParse(request);
      const blob = await response.blob();
      saveAs(blob, `${productId}extractRemainingCodes.csv`);
    },
  });
};

export const getDeleteRemainingCodes = ({
  productId,
  accessToken,
}: WithAccessToken<CsvUploadRequest>): Promise<CsvUploadResponse> => {
  const request = RequestFactory.createGetRequest(
    `/products/${productId}/vouchers/delete-remaining`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        ...prodServiceGatewayHeader,
      },
    },
  );

  return fetchAndParseJson(request);
};

export const useDeleteRemainingCodes = (productId?: string) => {
  const { accessToken } = useOktaAuth();

  return useMutation({
    mutationFn: async () => {
      const { presignedUrl } = await getDeleteRemainingCodes({
        productId,
        accessToken,
      });
      const request = RequestFactory.createGetCsvRequest(presignedUrl, {
        headers: {
          ...prodServiceGatewayHeader,
        },
      });
      const response = await fetchAndParse(request);
      const blob = await response.blob();
      saveAs(blob, `${productId}deletedCodes.csv`);
    },
  });
};
